import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import LocaleLink from '@/components/LocaleLink';
import { useLocale } from '@/hooks/useLocale';
import { LEGAL_DOCS_EN, type LegalBlock, type LegalDoc } from '@/copy/legal-en';
import { LEGAL_DOCS_AR } from '@/copy/legal-ar';
import './LegalPage.css';

interface LegalPageProps {
  docKey: LegalDoc['key'];
}

const SIBLINGS: Record<LegalDoc['key'], { to: string; label: string; labelAr: string }> = {
  privacy: { to: '/privacy', label: 'Privacy Policy', labelAr: 'سياسة الخصوصية' },
  terms: { to: '/terms', label: 'Terms of Service', labelAr: 'شروط الخدمة' },
  subprocessors: { to: '/subprocessors', label: 'Sub-processors', labelAr: 'مزودو الخدمات' },
};

const CHROME = {
  en: { onThisPage: 'On this page', related: 'Related legal pages' },
  ar: { onThisPage: 'في هذه الصفحة', related: 'صفحات قانونية ذات صلة' },
};

/* Inline parser → React nodes. Supports **bold** and [label](href). */
function renderInline(text: string): ReactNode {
  const re = /\*\*(.+?)\*\*|\[(.+?)\]\(([^)]+)\)/g;
  const nodes: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      nodes.push(<strong key={k++}>{m[1]}</strong>);
    } else {
      // External sites (http/https) open in a new tab so our page stays put;
      // mailto: links keep the default handling.
      const href = m[3] ?? '';
      const external = /^https?:\/\//i.test(href);
      nodes.push(
        <a
          key={k++}
          className="lgl-link"
          href={href}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {renderInline(m[2] ?? '')}
        </a>,
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function renderBlock(b: LegalBlock, i: number): ReactNode {
  switch (b.t) {
    case 'h':
      return (
        <h3 key={i} className="lgl-subhead">
          {renderInline(b.c)}
        </h3>
      );
    case 'ul':
      return (
        <ul key={i} className="lgl-list">
          {b.c.map((item, j) => (
            <li key={j}>{renderInline(item)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={i} className="lgl-list">
          {b.c.map((item, j) => (
            <li key={j}>{renderInline(item)}</li>
          ))}
        </ol>
      );
    case 'table':
      return (
        <div key={i} className="lgl-table-wrap">
          <table className="lgl-table">
            <thead>
              <tr>
                {b.head.map((h, j) => (
                  <th key={j}>{renderInline(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{renderInline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'note':
      return (
        <div key={i} className="lgl-note">
          {b.label && <span className="lgl-note__label">{b.label}</span>}
          <p className="lgl-note__body">{renderInline(b.c)}</p>
        </div>
      );
    case 'p':
    default:
      return (
        <p key={i} className="lgl-p">
          {renderInline(b.c)}
        </p>
      );
  }
}

function Arrow() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

export default function LegalPage({ docKey }: LegalPageProps) {
  // Show the Arabic document on /ar/ when it has been translated; otherwise
  // fall back to English (Terms & Sub-processors are still English-only) with
  // ltr direction so untranslated fallbacks stay readable.
  const { locale } = useLocale();
  const arDoc = LEGAL_DOCS_AR[docKey];
  const showAr = locale === 'ar' && arDoc !== undefined;
  const doc = showAr ? arDoc : LEGAL_DOCS_EN[docKey];
  const dir = showAr ? 'rtl' : 'ltr';
  const chrome = showAr ? CHROME.ar : CHROME.en;
  const [active, setActive] = useState(doc.sections[0]?.id ?? '');

  /* Deep-link landing — re-scroll to the URL hash after the webfonts settle.
   * On a fresh load of e.g. /en/terms#contact the browser's native anchor scroll
   * (and ScrollRestoration) fire while the fallback font is still showing; when
   * Plex swaps in, the reflow moves the target and the page is left scrolled
   * short. Re-running scrollIntoView once document.fonts.ready resolves lands the
   * clause where it belongs (respecting scroll-margin-block-start: 96px), or at
   * the scroll maximum for the final clause. In-page TOC clicks are handled
   * separately by jumpTo — this only fixes the initial load. */
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    let cancelled = false;
    const land = () => {
      if (cancelled) return;
      document.getElementById(id)?.scrollIntoView();
    };
    if ('fonts' in document) {
      document.fonts.ready.then(land).catch(() => {});
    }
    // Fallback for environments without the Font Loading API.
    const raf = requestAnimationFrame(land);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [docKey]);

  /* Scroll-spy — highlight the clause whose heading you have most recently
   * scrolled past (its top sits above a reference line just under the sticky
   * nav). This is a position query, deliberately NOT an IntersectionObserver
   * band: the old `-45% 0px -50% 0px` band sat at the vertical MIDDLE of the
   * viewport, so it lit the card 1-2 ahead of the one being read, never lit the
   * first section at the top (its card is above the band), and never lit the
   * tail sections (the page can't scroll them up to the middle). Verified with a
   * scroll harness — the band version mismatched at 29 of 32 sample positions. */
  useEffect(() => {
    const ids = doc.sections.map((s) => s.id);
    if (!ids.length) return;
    const LINE = 120; // px below the viewport top; clears the ~74px sticky nav
    let raf = 0;
    const compute = () => {
      raf = 0;
      // At the page bottom the tail cards can't reach LINE — pin the last one,
      // otherwise the final clauses could never be highlighted.
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      // ids is non-empty (guarded above), so the endpoints are defined —
      // noUncheckedIndexedAccess just can't see it through the length check.
      if (atBottom) {
        setActive(ids[ids.length - 1]!);
        return;
      }
      let current = ids[0]!;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= LINE) current = id;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [docKey, doc.sections]);

  /* Jump to a clause on TOC click. We do this ourselves instead of leaning on
   * the native <a href="#id"> scroll because <ScrollRestoration> (kept in
   * root.tsx for scroll-to-top on real navigations) intercepts the resulting
   * in-page location change and swallows the anchor scroll — verified: clicking
   * a link updated the URL hash but left the page at the wrong offset.
   * scrollIntoView honours scroll-margin-block-start (96px); replaceState
   * updates the URL for deep-linking WITHOUT notifying the router, so
   * ScrollRestoration never runs. Modifier/aux clicks fall through to the
   * browser so "open in new tab" still works (a fresh load reads the hash). */
  const jumpTo = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView();
    history.replaceState(null, '', `#${id}`);
    setActive(id);
  };

  const siblings = (Object.keys(SIBLINGS) as LegalDoc['key'][]).filter((k) => k !== docKey);

  return (
    <div className="lgl-page" dir={dir}>
      <header className="lgl-hero">
        <div className="mkt-container lgl-hero__inner">
          <p className="mkt-eyebrow" style={{ margin: 0 }}>
            {doc.eyebrow}
          </p>
          <h1 className="lgl-hero__title">{doc.title}</h1>
          <div className="lgl-hero__meta">
            <span>{doc.updated}</span>
          </div>
          <p className="lgl-hero__intro">{renderInline(doc.intro)}</p>
        </div>
      </header>

      <div className="mkt-container">
        <div className="lgl-body">
          <aside className="lgl-toc" aria-label={chrome.onThisPage}>
            <p className="lgl-toc__title">{chrome.onThisPage}</p>
            <ul className="lgl-toc__list">
              {doc.sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className={`lgl-toc__link ${active === s.id ? 'is-active' : ''}`}
                    onClick={(e) => jumpTo(e, s.id)}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          <div className="lgl-content">
            {doc.sections.map((s) => (
              <section key={s.id} id={s.id} className="lgl-section">
                <div className="lgl-section__head">
                  <span className="lgl-section__num">{s.n}</span>
                  <h2 className="lgl-section__title">{s.title}</h2>
                </div>
                {s.blocks.map(renderBlock)}
              </section>
            ))}

            <nav className="lgl-siblings" aria-label={chrome.related}>
              {siblings.map((k) => (
                <LocaleLink key={k} to={SIBLINGS[k].to} className="lgl-sibling">
                  {showAr ? SIBLINGS[k].labelAr : SIBLINGS[k].label}
                  <Arrow />
                </LocaleLink>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
